import { SidebarTexture } from "./SidebarTexture";
import { LivingCanvas, MotionControl, rememberPlace } from "./Living";
import { Users } from "lucide-react";
import { FolderTree, LevelPath } from "./screens/Explorer";
import { ModeSwitch, PlaygroundBar, useMode, FoldersTour } from "./screens/Playground";
import { Sheet } from "./Sheet";
import { GullPanel } from "./screens/GullPanel";
import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { bp, usePid } from "./slug";
import { LayoutGrid, Layers, Library, Settings2, Bell, ChevronRight, Activity, Menu, X, LogOut, Search, ListChecks, Database, Rows3, Sparkles, Telescope } from "lucide-react";
import { useAuth } from "./auth";
import { useLoad } from "./ui";
import { CommandPalette } from "./CommandPalette";
import { JobTray, useRuns } from "./JobTray";
import { Logo } from "./Logo";

export const WORKSPACES = [
  { id: "build", label: "Build", icon: LayoutGrid, screens: [["build", "Build"], ["numbers", "Numbers"]] },
  { id: "plan", label: "Plan", icon: ListChecks, screens: [["plan", "Dates and money"], ["flex", "Try a different arrangement"], ["economics", "Costs"],
    ["conditions", "What must be true"], ["resources", "Shared resources"], ["cash", "When money lands"], ["routes", "Changes and routes"], ["commitments", "Promises made"], ["notices", "Notices"], ["approval", "Agree the exact scope"],
    ["configure", "Deployment baseline"], ["remaining", "Still to do"], ["reuse", "Next block"], ["continuity", "Future move"], ["transition", "Transition"], ["recovery", "Recovery"], ["value", "Continuing value"], ["rights", "Rights"], ["lifetime", "Lifetime support"]] },
  { id: "files", label: "Documents", icon: Layers, screens: [["sources", "Documents"], ["agreements", "What LARUS read"], ["review", "Review proposals"], ["correction", "Corrections"]] },
  { id: "record", label: "Record", icon: Database, screens: [["record", "Record"], ["evidence", "Installed evidence"], ["examination", "Examination"], ["packet", "Packs and decisions"], ["handover", "Handover"]] },
  { id: "people", label: "People", icon: Users, screens: [["people", "People"]] },
] as const;

export const workspaceOf = (screen: string) => WORKSPACES.find((w) => w.screens.some((s) => s[0] === screen)) || WORKSPACES[0];
export const lastProgramme = () => localStorage.getItem("larus:programme") || "";

export function useCan(pid: string) {
  const { data } = useLoad<any>(`/programmes/${pid}`);
  return (action: string) => !!data && (data.my_actions || []).includes(action);
}

function initials(n: string) { return n.replace(/[^A-Za-z ]/g, "").split(/\s+/).filter(Boolean).map((x) => x[0]).join("").slice(0, 2).toUpperCase(); }

function Sidebar({ open, close, tree }: { open: boolean; close: () => void; tree?: React.ReactNode }) {
  const { user, logout } = useAuth();
  const u = user || { name: "", entity_name: "" };
  return (
    <aside className={`sidebar open-tree ${open ? "open" : ""} pinned`} data-testid="sidebar" data-pinned="true">
      <SidebarTexture />
      <div className="spread"><Link to="/" onClick={close} style={{ textDecoration: "none" }}><Logo /></Link>
        <button className="ghost mobile-only" aria-label="Close menu" onClick={close} data-testid="sidebar-close-btn"><X size={18} /></button></div>
      {tree}
      <details className="navmenu" data-testid="nav-workspace-menu" open={!!localStorage.getItem("larus:nav-menu-open")} onToggle={(e) => localStorage.setItem("larus:nav-menu-open", (e.target as HTMLDetailsElement).open ? "1" : "")}><summary className="navgroup">MORE</summary>
      <nav aria-label="Workspace">
        <NavLink to="/overview" onClick={close} data-testid="nav-overview"><Telescope size={17} /><span>Overview</span></NavLink>
        <NavLink to="/portfolio" onClick={close} data-testid="nav-portfolio"><Rows3 size={17} /><span>All builds</span></NavLink>
        <NavLink to="/library" onClick={close} data-testid="nav-library"><Library size={17} /><span>Library</span></NavLink>
        <NavLink to="/operations" onClick={close} data-testid="nav-operations"><Activity size={17} /><span>Workspace health</span></NavLink>
        <NavLink to="/settings" onClick={close} data-testid="nav-settings"><Settings2 size={17} /><span>Settings</span></NavLink>
      </nav></details>
      <div className="sidefoot">
        <div className="row"><span className="avatar dark">{initials(u.name || "?")}</span><div><strong data-testid="sidebar-user-name">{u.name}</strong>{u.entity_name}</div></div>
        <button className="ghost signout" onClick={logout} data-testid="logout-btn"><LogOut size={14} /> Sign out</button>
        <div className="sidefoot-note">Invitation only</div>
      </div>
    </aside>
  );
}

export function Shell({ children, crumb, mode, tree }: { children: React.ReactNode; crumb?: string; mode?: string; tree?: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [palette, setPalette] = useState(false);
  const [tray, setTray] = useState<{ open: boolean; id?: string }>({ open: false });
  const { user } = useAuth();
  const pid = usePid();
  const prog = pid || lastProgramme();
  const { runs } = useRuns(prog || undefined);
  const live = (runs || []).filter((r) => r.status === "QUEUED" || r.status === "RUNNING").length;
  const modeNow = useMode();
  const explorer = useLoad<any>(`/explorer?mode=${modeNow}`).data;
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setPalette(true); } };
    const t = (e: Event) => setTray({ open: true, id: (e as CustomEvent).detail });
    window.addEventListener("keydown", h);
    window.addEventListener("larus:tray", t);
    return () => { window.removeEventListener("keydown", h); window.removeEventListener("larus:tray", t); };
  }, []);
  const loc2 = useLocation();
  useEffect(() => { if (pid) rememberPlace(loc2.pathname); }, [loc2.pathname, pid]);
  return (
    <>
      <LivingCanvas />
      <a href="#main-content" className="skiplink">Skip to content</a>
      <Sidebar open={open} close={() => setOpen(false)} tree={tree || <FolderTree data={explorer} close={() => setOpen(false)} />} />
      {open && <div className="scrim" onClick={() => setOpen(false)} />}
      <div className="shell">
        <header className="topbar">
          <div className="crumb"><button className="ghost mobile-only" aria-label="Open menu" aria-expanded={open} aria-controls="sidebar" onClick={() => setOpen(true)} data-testid="mobile-menu-btn"><Menu size={18} aria-hidden /></button>
            <b className="crumb-current" data-testid="crumb-current">{crumb || "Everything"}</b>
</div>
          <ModeSwitch />
          <div className="row tb-actions"><MotionControl />
            <NavLink to="/overview" className={({ isActive }) => `tb-btn ${isActive ? "on" : ""}`} data-testid="topbar-overview"><Telescope size={15} aria-hidden /><span>Overview</span></NavLink>
            {prog && <button className="tb-btn gull" onClick={() => window.dispatchEvent(new CustomEvent("larus:gull"))} aria-label="Ask Gull for help" data-testid="topbar-gull"><Sparkles size={15} aria-hidden /><span>Help me with this</span></button>}
            <button className="tb-btn" onClick={() => setPalette(true)} aria-label="Search (Ctrl K)" data-testid="open-command-palette-btn"><Search size={15} aria-hidden /><span className="hide-sm">Search</span><kbd className="hide-sm">⌘K</kbd></button>
            {prog && <button className="ghost traybtn" aria-label="Work in progress" onClick={() => setTray({ open: true })} data-testid="tray-btn"><ListChecks size={17} />{live > 0 && <span className="count">{live}</span>}</button>}
            <Link to="/operations" className="ghost" aria-label="Notifications" data-testid="notifications-link"><Bell size={17} /></Link>
            <span className="avatar" role="img" aria-label={user ? `Signed in as ${user.name}` : "Account"} title={user ? user.email : ""}>{user ? initials(user.name) : ""}</span>
          </div>
        </header>
        <PlaygroundBar pid={pid} isDemo={mode === "demo"} />
        <FoldersTour />
        <main className="main" id="main-content" tabIndex={-1}>{children}
          <footer className="foot"><span>CANUS / LARUS</span><span>Private workspace · Invitation only</span></footer>
        </main>
      </div>
      {palette && <CommandPalette close={() => setPalette(false)} />}
      {tray.open && prog && <JobTray pid={prog} initial={tray.id} close={() => setTray({ open: false })} />}
      <GullSheet pid={prog || undefined} />
    </>
  );
}

export function ProgrammeFrame({ screen, title, subtitle, actions, children, plain }: { screen: string; title: string; subtitle?: React.ReactNode; actions?: React.ReactNode; children: React.ReactNode; plain?: boolean }) {
  const pid = usePid();
  const nav = useNavigate();
  const { data: p, error, reload } = useLoad<any>(pid ? `/programmes/${pid}` : null);
  const folder = useLoad<any>(pid ? `/programmes/${pid}/folder` : null).data;
  useEffect(() => { if (pid && p) { localStorage.setItem("larus:programme", pid); sessionStorage.setItem("larus:progName", p.name); } }, [pid, p]);
  useEffect(() => {
    window.addEventListener("larus:changed", reload);
    return () => { window.removeEventListener("larus:changed", reload); };
  }, [reload]);
  const ws = workspaceOf(screen)!;
  if (error) return <Shell><div className="emptyline" data-testid="programme-denied">This does not exist or you are not permitted to see it.<p><button onClick={() => nav("/")}>Back</button></p></div></Shell>;
  const pg = folder?.programme;
  return (
    <Shell crumb={pg ? `Build ${pg.build_no} · ${pg.root_name}` : p?.name} mode={p?.environment === "demo" ? "demo" : p ? "workspace" : undefined}>
      <div data-testid="programme-crumbs">{pg ? <LevelPath org={{ id: pg.organisation_id, name: pg.organisation }} prog={{ id: pg.root_id, name: pg.root_name }} build={{ id: pid!, no: pg.build_no, tone: folder?.waiting_on ? "needs" : folder?.details?.state === "CLEAR" ? "clear" : folder?.details?.state === "NOT_ASSESSED" ? "idle" : "held" }} tail={ws.id === "build" ? undefined : ws.label} /> : <LevelPath />}</div>
      {!plain && <div className="head"><div><h1 data-testid="screen-title">{title}</h1>
        <p className="subtitle">{subtitle}</p>
        {p && <p className="subtitle tiny" data-testid="programme-header">{p.customer} · {p.campus} · you are here as {ROLE_WORDS[p.my_role] || p.my_role.replace(/_/g, " ")}</p>}
      </div><div className="toolbar">{actions}</div></div>}
      {ws.screens.length > 1 && ws.id !== "plan" && ws.id !== "build" && <div className="subtabs">{ws.screens.map(([id, l]) => <Link key={id} to={`${bp(pid)}/${id}`} className={id === screen ? "active" : ""} data-testid={`screen-tab-${id}`}>{l}</Link>)}</div>}
      {children}
    </Shell>
  );
}
const ROLE_WORDS: Record<string, string> = { programme_admin: "the coordinator", recipient_decision_maker: "the customer", commercial_owner: "the lender", source_reviewer: "a site partner", engineering_owner: "the engineer", examiner: "the examiner", participant_viewer: "an observer" };


function GullSheet({ pid }: { pid?: string }) {
  const [open, setOpen] = useState(false);
  useEffect(() => { const h = () => setOpen(true); window.addEventListener("larus:gull", h); return () => window.removeEventListener("larus:gull", h); }, []);
  const close = React.useCallback(() => setOpen(false), []);
  if (!pid) return null;
  return <Sheet open={open} onClose={close} title="Help with this build" testid="gull-sheet"><GullPanel pid={pid} onChanged={() => window.dispatchEvent(new CustomEvent("larus:reload"))} /></Sheet>;
}

